import type { QueryClient } from "@tanstack/react-query";

import {
  buildSharedSessionExistsQueryKey,
  buildSharedSessionsQueryKey,
  type SharedSessionTarget,
} from "./shared-session-cache-utils";
import type { SharedSession } from "./types";

import { API_URL } from "@/api/application-api";

export interface CreateDashboardSharedSessionResponse {
  success: boolean;
  shared_session: SharedSession;
}

export const postDashboardSharedSession = async (
  userToken: string | null | undefined,
  workspaceId: number,
  body: Record<string, unknown>,
): Promise<CreateDashboardSharedSessionResponse> => {
  const response = await fetch(
    `${API_URL}/api/dashboard/v2/workspaces/${workspaceId}/shared_sessions`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      credentials: "include",
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to create shared session");
  }

  return response.json();
};

export const applyCreateDashboardSharedSessionToCache = (
  queryClient: QueryClient,
  data: CreateDashboardSharedSessionResponse,
  target: SharedSessionTarget,
) => {
  const newSession = data.shared_session;
  const sessionsKey = buildSharedSessionsQueryKey(target);

  queryClient.setQueryData<SharedSession[]>(sessionsKey, old =>
    old ? [...old, newSession] : [newSession],
  );

  queryClient.setQueryData(buildSharedSessionExistsQueryKey(target), true);

  queryClient.invalidateQueries({ queryKey: sessionsKey });
  if (target.workspaceId !== undefined) {
    queryClient.invalidateQueries({
      queryKey: ["shared_sessions", "exists", target.workspaceId],
    });
  }
};
